import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import Swal from 'sweetalert2';

const OnboardingPayment = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Get onboarding data from localStorage
  const onboardingData = JSON.parse(localStorage.getItem('onboardingFormData') || '{}');
  const paymentData = JSON.parse(localStorage.getItem('onboardingPaymentData') || '{}');
  
  const { userId, fullName, email } = onboardingData;
  const { amount = 1000, userName, userEmail, plan = 'Annually' } = paymentData;

  const handlePayment = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');

    if (!stripe || !elements) {
      setMessage('Stripe has not loaded yet.');
      setLoading(false);
      return;
    }

    if (!userId) {
      setMessage('User ID not found. Please restart the onboarding process.');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    // Create payment method with real card details
    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: userName || fullName,
        email: userEmail || email,
      },
    });

    if (paymentMethodError) {
      setMessage(paymentMethodError.message);
      setLoading(false);
      return;
    }

    try {
      // Create payment intent using selected amount (in cents)
      const paymentIntentResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/create-payment-intent`, {
        userId: parseInt(userId),
        userName: userName || fullName,
        userEmail: userEmail || email,
        amount: Math.round((amount || 0) * 100),
      });

      console.log('Payment intent response:', paymentIntentResponse.data);

      if (!paymentIntentResponse.data || !paymentIntentResponse.data.clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret } = paymentIntentResponse.data;

      // Confirm payment with real card
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id,
      });

      if (confirmError) {
        setMessage(confirmError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Debug: Log the full paymentIntent object to see its structure
        console.log('Stripe Payment Intent after success:', paymentIntent);
        console.log('Payment Intent charges:', paymentIntent.charges);
        console.log('Payment Intent latest_charge:', paymentIntent.latest_charge);
        
        // Save payment to database with real card details
        const startDate = new Date();
        const endDate = new Date();
        endDate.setFullYear(endDate.getFullYear() + 1);
        
        // Try different ways to get the receipt URL
        let receiptUrl = '';
        
        // Debug: Log the full paymentIntent structure
        console.log('Full paymentIntent object:', JSON.stringify(paymentIntent, null, 2));
        console.log('paymentIntent.charges:', paymentIntent.charges);
        console.log('paymentIntent.latest_charge:', paymentIntent.latest_charge);
        
        // Try multiple ways to extract receipt URL
        if (paymentIntent.charges?.data?.[0]?.receipt_url) {
          receiptUrl = paymentIntent.charges.data[0].receipt_url;
          console.log('Found receipt URL in charges.data[0]:', receiptUrl);
        } else if (paymentIntent.latest_charge?.receipt_url) {
          receiptUrl = paymentIntent.latest_charge.receipt_url;
          console.log('Found receipt URL in latest_charge:', receiptUrl);
        } else if (paymentIntent.receipt_url) {
          receiptUrl = paymentIntent.receipt_url;
          console.log('Found receipt URL in paymentIntent:', receiptUrl);
        } else if (paymentIntent.charges?.data?.[0]?.receipt_url) {
          receiptUrl = paymentIntent.charges.data[0].receipt_url;
          console.log('Found receipt URL in charges.data[0] (second check):', receiptUrl);
        } else {
          // Generate a default receipt URL based on payment intent ID
          receiptUrl = `https://pay.stripe.com/receipts/${paymentIntent.id}`;
          console.log('No receipt URL found, using default:', receiptUrl);
        }
        
        console.log('Final extracted receipt URL:', receiptUrl);
        
        const saveData = {
          subscriptionId: `sub_onboarding_${userId}_${Date.now()}`,
          userId: parseInt(userId),
          userName: userName || fullName,
          userEmail: userEmail || email,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          subscriptionType: plan || 'Annually',
          paymentIntentId: paymentIntent.id,
          cardLast4: paymentMethod.card.last4,
          cardBrand: paymentMethod.card.brand,
          receiptUrl: receiptUrl
        };
        
        console.log('Saving subscription with real card details:', saveData);
        
        // Use onboarding-specific payment endpoint with real card details
        const saveResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/save-onboarding-payment`, saveData);
        
        if (saveResponse.data && saveResponse.data.message) {
          Swal.fire({
            icon: 'success',
            title: 'Payment Successful!',
            html: `Your $${amount}/${plan === 'Annually' ? 'year' : 'month'} subscription has been activated.<br/><br/>Please logout and login again to get all functionalities.`,
            confirmButtonText: 'Continue',
            confirmButtonColor: '#2e3192'
          }).then(() => {
            // Return to onboarding step 6
            navigate('/onboarding?step=6');
          });
        } else {
          throw new Error('Failed to save subscription');
        }
      } else {
        throw new Error(`Payment failed with status: ${paymentIntent.status}`);
      }
      
    } catch (error) {
      console.error('Payment processing error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'There was an error processing your payment. Please try again.';
      
      setMessage(errorMessage);
      
      Swal.fire({
        icon: 'error',
        title: 'Payment Failed',
        text: errorMessage,
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding?step=4');
  };

  return (
    <div className="home-container">
      <Container fluid className="px-3 py-4">
        <div className="card shadow-sm p-3 mt-3" style={{ border: "none", backgroundColor: "#fff" }}>
          <div className="card-header" style={{ border: "none", backgroundColor: "#fff" }}>
            <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
              <button 
                className="btn btn-link p-0 me-3" 
                onClick={handleBack}
                style={{ color: '#2e3192', textDecoration: 'none' }}
              >
                <FaArrowLeft />
              </button>
              Payment
            </h5>
          </div>
          <hr />

          <div className="container">
            <div className="row mt-2">
              <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                <div className="mt-3">
                  <input 
                    type="text" 
                    placeholder="Email" 
                    value={userEmail || email || ''} 
                    className="form-control" 
                    disabled 
                  />
                </div>
                <div className="mt-3">
                  <input 
                    type="text" 
                    placeholder="Name" 
                    value={userName || fullName || ''} 
                    className="form-control" 
                    disabled 
                  />
                </div>
                <form onSubmit={handlePayment}>
                  <div className="mt-3">
                    <input 
                      type="text" 
                      placeholder="Amount" 
                      value={`$${amount}`} 
                      className="form-control" 
                      disabled 
                    />
                  </div>

                  <div className="mt-3">
                    <label className="form-label">Card Details</label>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': {
                              color: '#aab7c4',
                            },
                          },
                        },
                      }}
                    />
                  </div>

                  <div className="mt-3">
                  <Alert variant="info">
                    <strong>Onboarding Payment:</strong> This is a ${`${amount}/${plan === 'Annually' ? 'year' : 'month'}`} subscription for your KnackVibes account.
                    </Alert>
                  </div>

                  <div className="btn-group mt-3">
                    <Button 
                      type="submit"
                      variant="primary" 
                      disabled={!stripe || loading}
                      style={{ backgroundColor: '#2e3192', borderColor: '#2e3192' }}
                    >
                      {loading ? "Processing..." : `Pay $${amount}`}
                    </Button>
                  </div>
                </form>

                {message && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {message}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default OnboardingPayment;
